import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Locale ('vi' | 'en') is managed here and persisted to localStorage. The
// server language dictionary is Vietnamese-first; for English we merge an EN
// override map on top (see buildLang / EN_OVERRIDES). Both the React context
// (useLang) AND the `lang` prop threaded through App receive the *merged*
// dictionary, so components using either style get translated strings.

import { EN_OVERRIDES } from './langOverrides';

const LangContext = createContext({});
const LocaleContext = createContext({ locale: 'vi', setLocale: () => {} });

const STORAGE_KEY = 'isp_locale';

export const getInitialLocale = () => {
  try { return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'vi'; } catch (_) { return 'vi'; }
};

// Merge overrides onto the base dictionary for the given locale.
export const buildLang = (base, locale) =>
  locale === 'en' ? { ...(base || {}), ...EN_OVERRIDES } : (base || {});

// Locale provider — holds the locale state and exposes setLocale.
export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getInitialLocale);
  const setLocale = useCallback((loc) => {
    const next = loc === 'en' ? 'en' : 'vi';
    setLocaleState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) {}
  }, []);
  useEffect(() => {
    try { if (document?.documentElement) document.documentElement.lang = locale; } catch (_) {}
  }, [locale]);
  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>{children}</LocaleContext.Provider>
  );
};

// Lang provider — receives the already-merged dictionary from App.
export const LangProvider = ({ value, children }) => (
  <LangContext.Provider value={value || {}}>{children}</LangContext.Provider>
);

export const useLang = () => useContext(LangContext) || {};
export const useLocale = () => useContext(LocaleContext);

export default LangContext;
